const activeAnimations = new WeakMap<HTMLElement, number>();

export const smoothScroll = (
  element: HTMLElement,
  startPosition: number,
  targetPosition: number,
  duration: number,
  onComplete?: () => void,
): void => {
  const prevId = activeAnimations.get(element);
  if (prevId) {
    cancelAnimationFrame(prevId);
  }

  const startTime = performance.now();

  const animateScroll = (currentTime: number) => {
    const elapsedTime = currentTime - startTime;

    if (elapsedTime < duration) {
      const progress = elapsedTime / duration;
      const easing = progress < 0.5 ? 4 * progress * progress * progress : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      const position = startPosition + (targetPosition - startPosition) * easing;
      element.scrollLeft = Math.round(position);

      const newId = requestAnimationFrame(animateScroll);
      activeAnimations.set(element, newId);
    } else {
      element.scrollLeft = targetPosition;
      activeAnimations.delete(element); // Clean up when animation completes
      onComplete?.();
    }
  };

  const rafId = requestAnimationFrame(animateScroll);
  activeAnimations.set(element, rafId);
};

export const DEFAULT_SCROLL_DURATION = 600;

export type ScrollDirection = 'left' | 'right';

/**
 * Scroll a container horizontally by one viewport width in the specified direction
 * @param container The HTML element to scroll
 * @param direction The direction to scroll: 'left' or 'right'
 * @param duration The duration of the scroll animation in milliseconds
 * @param onComplete Callback function to execute when scrolling is complete
 */
export const scrollHorizontally = (
  container: HTMLElement,
  direction: ScrollDirection,
  duration = DEFAULT_SCROLL_DURATION,
  onComplete?: () => void,
): void => {
  const { clientWidth, scrollWidth, scrollLeft } = container;
  const startPosition = scrollLeft;

  let targetPosition: number;

  if (direction === 'left') {
    targetPosition = Math.max(startPosition - clientWidth, 0);
  } else {
    targetPosition = Math.min(startPosition + clientWidth, scrollWidth - clientWidth);
  }

  smoothScroll(container, startPosition, targetPosition, duration, onComplete);
};
